import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Search } from 'components/search';

import { ICasino, IProvider, ISlot, IStreamer } from 'constants/types';
import { useClickOutside } from 'hooks/useClickOutside';
import { useFocus } from 'hooks/useFocus';
import { useRouter } from 'next/router';
import { useSearchQuery, useSearchStreamersQuery } from 'hooks/api/search';
import {
  getDropdownAllResults,
  getDropdownSearchParams,
} from 'utils/search-query';

import styles from './styles.module.css';

const SearchSkeleton = ({ count = 3 }) => {
  return (
    <div className={styles.skeleton_container}>
      {[...Array(count)].map((_, index) => (
        <div key={index} className={styles.skeleton_item}>
          <div className={styles.skeleton_image}>
            <div className={styles.skeleton_shine} />
          </div>
          <div className={styles.skeleton_content}>
            <div className={styles.skeleton_title}>
              <div className={styles.skeleton_shine} />
            </div>
            <div className={styles.skeleton_subtitle}>
              <div className={styles.skeleton_shine} />
            </div>
          </div>
          <div className={styles.skeleton_arrow}>
            <div className={styles.skeleton_shine} />
          </div>
        </div>
      ))}
    </div>
  );
};

interface ResultSearchProps {
  variant?: string;
  searchText?: string;
  searchRef: React.RefObject<HTMLInputElement | null>;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  type?: 'modal' | 'bar';
  searchPosition?: 'absolute' | 'relative';
}

export const ResultSearch = ({
  variant,
  searchRef,
  searchText,
  containerRef,
  searchPosition,
  type = 'modal',
}: ResultSearchProps) => {
  const router = useRouter();
  const search = router.query.q
    ? String(decodeURIComponent(router.query.q?.toString()))
    : (searchText ?? '');

  const ref = useRef(null);
  const [isResult, setIsResult] = useState(false);

  const handleCloseResult = () => {
    setIsResult(false);
  };

  useClickOutside({ onClick: handleCloseResult, ref, targetRef: containerRef });

  const { data: dataSearch, isFetching: dataIsFetching } = useSearchQuery(
    {
      searchQuery: search.toLowerCase(),
      ...getDropdownSearchParams(variant),
    },
    { skip: !search || variant === 'streamer' }
  );

  const { data: dataStreamers, isFetching: isFetchingStreamers } =
    useSearchStreamersQuery(
      {
        searchQuery: search.toLowerCase(),
      },
      { skip: !search || variant !== 'streamer' }
    );

  const data = variant === 'streamer' ? dataStreamers : dataSearch;
  const isFetching =
    variant === 'streamer' ? isFetchingStreamers : dataIsFetching;

  const focused = useFocus(searchRef);

  const results: Array<ICasino | ISlot | IProvider | IStreamer> =
    useMemo(() => {
      if (search) {
        return [
          ...(data?.results?.casinos ?? []),
          ...(data?.results?.slots ?? []),
          ...(data?.results?.streamers ?? []),
        ];
      } else {
        return [];
      }
    }, [data, search]);

  useEffect(() => {
    const hasSearch = !!search?.trim().length;

    if (!hasSearch) {
      setIsResult(false);
      return;
    }

    // On real mobile devices focus can momentarily drop when keyboard/layout shifts.
    // Keep results open while user has an active query or request in progress.
    if (focused || isFetching || !!searchText?.trim().length) {
      setIsResult(true);
    }
  }, [focused, isFetching, search, searchText]);

  return (
    <>
      {isFetching && search && (
        <div ref={ref} className="search-results">
          <SearchSkeleton count={4} />
        </div>
      )}
      {isResult && search && !isFetching && (
        <div ref={ref} className="search-results">
          <div className={styles.resultAnchor}>
            <Search
              searchPosition={searchPosition}
              variant={variant}
              data={results as any}
              handleClose={handleCloseResult}
              allResults={getDropdownAllResults(search, data?.counts, variant)}
              type={type}
              wrapperStyle={{ position: 'static', width: '100%' }}
            />
          </div>
        </div>
      )}
    </>
  );
};
